<script setup lang="ts">
import { ARTICLE_FAVORITE_TOGGLE_ERROR } from '@/pages/journalArticle/lib/journalArticleMessages';
import FavoriteButton from '@/features/FavoriteButton/index';
import LawyerCardShareList from '@/widgets/LawyerCard/ui/LawyerCardShareList.vue';
import ArticleBlocks from '@/pages/journalArticle/ui/ArticleBlocks.vue';
import JournalArticleSocial from '@/pages/journalArticle/ui/JournalArticleSocial.vue';

import { ROOT_URL_HOST } from '@/shared/constants/contactConstants';
import { FAVORITE_TYPE_ARTICLE } from '@/shared/constants/favoriteConstants';
import { modifyDate } from '@/shared/utils/modifyTime';
import { copyToClipboard } from '@/shared/utils/copyToClipboard';
import { scroll } from '@/shared/utils/scrollBehavior';
import { getArticleTextStats } from '@/pages/journalArticle/lib/articleText';
import { buildAuthorInfo } from '@/pages/journalArticle/lib/buildAuthorInfo';

import type {
  IJournalArticle,
  IJournalEditorBlock,
} from '@/entities/journal/model/types';
import { useErrorHandler } from '@/shared/lib/errors';

const props = defineProps<{
  article: IJournalArticle;
}>();

const { $favoriteService } = useNuxtApp();
const route = useRoute();

const isMobile: boolean = useIsMobile();
const articleUrl: ComputedRef<string> = computed(() => `${ROOT_URL_HOST}${route.path}`);
const { handleError } = useErrorHandler();
const isShowShareMenu: Ref<boolean> = ref(false);
const isFavorite: Ref<boolean> = ref(Boolean(props.article.is_favorite));

const editorBlocks: ComputedRef<IJournalEditorBlock[]> = computed(
  () => props.article.content?.blocks ?? [],
);
const pageViewsNumber: ComputedRef<number> = computed(
  () => props.article.views ?? props.article.view_count ?? 0,
);
const articleImage: ComputedRef<string> = computed(
  () => props.article.content?.preview || `${ROOT_URL_HOST}/og-image.png`,
);
const aboutName: ComputedRef<string> = computed(() => props.article.topic?.name ?? '');
const lawyer = computed(() => props.article.user?.lawyer ?? null);
const user = computed(() => props.article.user ?? null);
const authorInfo = computed(() => buildAuthorInfo(lawyer.value, user.value));
const authorId: ComputedRef<string> = computed(
  () => `${ROOT_URL_HOST}${authorInfo.value?.profileLink ?? ''}`,
);
const authorImage: ComputedRef<string> = computed(() => lawyer.value?.urlAvatar ?? '');

const editorText = computed(() => getArticleTextStats(editorBlocks.value));

const SECOND_LEVEL_HEADING = 2;
const articleHeadings: ComputedRef<IJournalEditorBlock[]> = computed(() =>
  editorBlocks.value.filter(
    (block) => block.type === 'header' && block.data.level === SECOND_LEVEL_HEADING,
  ),
);

const scrollToHeading = (id?: string): void => {
  if (!id) return;
  scroll(document.querySelector(`[data-id="${id}"]`));
};

const toggleFavorite = async (value: boolean): Promise<void> => {
  isFavorite.value = value;

  try {
    if (value) {
      await $favoriteService.addToFavorite(props.article.id, FAVORITE_TYPE_ARTICLE);
    } else {
      await $favoriteService.deleteFromFavorite(props.article.id, FAVORITE_TYPE_ARTICLE);
    }
  } catch (error) {
    isFavorite.value = !value;
    handleError(error, {
      context: { action: ARTICLE_FAVORITE_TOGGLE_ERROR },
    });
  }
};

const showShareMenu = (): void => {
  isShowShareMenu.value = true;
};

const hideShareMenu = (): void => {
  isShowShareMenu.value = false;
};

const copyUrl = async (): Promise<void> => {
  await copyToClipboard(window.location.href);
  isShowShareMenu.value = false;
};
</script>

<template>
  <article
    :class="['journal-article', { _mobile: isMobile }]"
    itemscope
    itemtype="https://schema.org/BlogPosting"
  >
    <meta itemprop="image" :content="articleImage" />
    <meta itemprop="wordCount" :content="String(editorText.wordCount)" />
    <meta itemprop="inLanguage" content="ru-RU" />
    <link itemprop="mainEntityOfPage" :href="articleUrl" />
    <link itemprop="url" :href="articleUrl" />
    <span
      class="journal-article__schema"
      itemprop="publisher"
      itemscope
      itemtype="https://schema.org/Organization"
    >
      <meta itemprop="name" content="Юрик.ру" />
      <meta itemprop="url" :content="ROOT_URL_HOST" />
      <span itemprop="logo" itemscope itemtype="https://schema.org/ImageObject">
        <meta itemprop="url" :content="`${ROOT_URL_HOST}/og-image.png`" />
      </span>
    </span>
    <span
      v-if="aboutName || articleImage"
      class="journal-article__schema"
      itemprop="about"
      itemscope
      itemtype="https://schema.org/LegalService"
    >
      <meta v-if="aboutName" itemprop="name" :content="aboutName" />
      <meta v-if="articleImage" itemprop="image" :content="articleImage" />
    </span>
    <span
      class="journal-article__schema"
      itemprop="speakable"
      itemscope
      itemtype="https://schema.org/SpeakableSpecification"
    >
      <meta itemprop="cssSelector" content=".journal-article__title" />
      <meta itemprop="cssSelector" content=".journal-article__subtitle" />
    </span>
    <div class="journal-article__header">
      <ul class="journal-article__info">
        <li class="journal-article__info-item _date">
          <div class="journal-article__views">
            <AppIcon class="journal-article__icon _eye" name="EyeSmall" />
            {{ pageViewsNumber }}
          </div>
          <div v-if="article.published_at" class="journal-article__date">
            Дата публикации:
            <time itemprop="datePublished" :content="article.published_at">
              {{ modifyDate(article.published_at) }}
            </time>
          </div>
          <div v-if="article.updated_at" class="journal-article__update-date">
            Обновлено:
            <time itemprop="dateModified" :content="article.updated_at">
              {{ modifyDate(article.updated_at) }}
            </time>
          </div>
        </li>
        <li
          v-if="lawyer || user"
          class="journal-article__info-item"
          itemprop="author"
          itemscope
          itemtype="https://schema.org/Person"
          :itemid="authorId"
        >
          <meta itemprop="jobTitle" :content="authorInfo?.jobTitle" />
          <meta v-if="aboutName" itemprop="knowsAbout" :content="aboutName" />
          <meta v-if="authorImage" itemprop="image" :content="authorImage" />
          <span class="journal-article__author">Автор:&nbsp;</span>
          <NuxtLink
            class="journal-article__link"
            :to="authorInfo?.profileLink"
            itemprop="url"
          >
            <span itemprop="name">{{ authorInfo?.fullName }}</span>
          </NuxtLink>
        </li>
      </ul>
      <div class="journal-article__actions">
        <div class="journal-article__favorite">
          <FavoriteButton
            :model-value="isFavorite"
            @update:model-value="toggleFavorite"
          />
        </div>
        <div v-click-outside="hideShareMenu" class="journal-article__share-wrapper">
          <button
            class="journal-article__button"
            type="button"
            aria-label="Поделиться статьёй"
            @click.prevent="showShareMenu"
          >
            <AppIcon class="journal-article__icon _share" name="Share" />
          </button>
          <div v-if="isShowShareMenu" class="journal-article__share">
            <LawyerCardShareList is-article @close="hideShareMenu" @copy="copyUrl" />
          </div>
        </div>
      </div>
    </div>
    <div class="journal-article__content">
      <div class="journal-article__content-header">
        <div class="journal-article__content-item _title">
          <h1 class="journal-article__title" itemprop="headline">
            {{ article.content?.title }}
          </h1>
          <div class="journal-article__subtitle" itemprop="description">
            {{ article.content?.description }}
          </div>
        </div>
        <div v-if="article.content?.preview" class="journal-article__content-item _image">
          <NuxtImg
            class="journal-article__image _cover"
            :src="article.content.preview"
            :alt="article.content?.title ?? 'Обложка статьи'"
            width="1000"
            densities="x1 x2"
            loading="eager"
            fetchpriority="high"
            :preload="{ fetchPriority: 'high' }"
          />
        </div>
        <div v-if="articleHeadings.length" class="journal-article__content-item _text">
          <h2 class="journal-article__heading">Содержание</h2>
          <ul class="journal-article__list _circle">
            <li
              v-for="heading in articleHeadings"
              :key="heading.id"
              class="journal-article__list-item _link"
            >
              <button
                class="journal-article__list-button"
                type="button"
                :aria-label="heading.data.text"
                @click.prevent="scrollToHeading(heading.id)"
              >
                {{ heading.data.text }}
              </button>
            </li>
          </ul>
        </div>
      </div>
      <meta itemprop="articleBody" :content="editorText.articleBody" />
      <ArticleBlocks class="journal-article__content-body" :blocks="editorBlocks" />
      <div class="journal-article__content-item">
        <JournalArticleSocial @copy="copyUrl" />
      </div>
    </div>
  </article>
</template>

<style lang="less">
.journal-article {
  .flex-column(@size-x10);

  &__schema {
    display: none;
  }

  &__content {
    .flex-column(@size-x5);
  }

  &__content-header {
    .flex-column(@size-x10);
  }

  &__content-item {
    &._title {
      .flex-column(@size-x3);
    }

    &._text {
      .flex-column(@size-x4);
    }

    &._image {
      display: flex;
    }
  }

  &__title {
    .h-offset-reset();

    font-size: 30px;
    line-height: 39px;
    font-weight: 700;
    word-break: break-word;
  }

  &__subtitle {
    font-size: @size-x4;
    line-height: 21px;
    font-weight: 700;
    color: @color-silver-chalice;
    max-width: 765px;
    word-break: break-word;
  }

  &__header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
  }

  &__share-wrapper {
    position: relative;
  }

  &__share {
    position: absolute;
    top: 0;
    right: 0;
    z-index: 10;
  }

  &__info {
    .flex-column(@size-x2);

    font-size: 14px;
    line-height: 18px;
    font-weight: 500;
  }

  &__info-item {
    &._date {
      display: inline-flex;
      align-items: center;
      gap: @size-x5;
    }
  }

  &__views {
    display: flex;
    align-items: center;
    gap: @main-size;
    color: @color-silver-chalice;
  }

  &__date,
  &__update-date {
    color: @color-silver-chalice;
  }

  &__author {
    color: @color-silver-chalice;
  }

  &__link {
    font-weight: 600;
    text-decoration: underline;

    &:hover {
      text-decoration: none;
    }
  }

  &__actions {
    display: flex;
    align-items: center;
    gap: @size-x2;
  }

  &__button {
    .button-reset();
  }

  &__icon {
    &._eye {
      width: 15px;
      height: 15px;
    }

    &._share {
      width: @size-x5;
      height: @size-x5;
    }
  }

  &__image {
    max-width: 100%;
    max-height: 425px;
    object-fit: contain;

    &._cover {
      width: 100%;
      height: 380px;
      object-fit: cover;
      border-radius: @main-size;
    }
  }

  &__heading {
    .h-offset-reset();

    font-size: @size-x6;
    line-height: 31px;
    font-weight: 700;
  }

  &__list {
    .flex-column(@size-x3);

    &._circle {
      list-style: disc;
      padding-left: @size-x4;
    }
  }

  &__list-item {
    font-size: @size-x4;
    line-height: 22px;
    font-weight: 400;

    &._link {
      &::marker {
        color: @color-persimmon;
      }
    }
  }

  &__list-button {
    .button-reset();

    display: inline-flex;
    text-align: left;
    font-size: 18px;
    line-height: 25px;
    font-weight: 600;
    color: @color-persimmon;
  }

  &._mobile &__header {
    align-items: flex-start;
  }

  &._mobile &__views {
    margin-right: @size-x4;
  }

  &._mobile &__info {
    flex-wrap: wrap;
    gap: @main-size;
    max-width: 260px;
  }

  &._mobile &__info-item {
    &._date {
      flex-wrap: wrap;
      gap: @main-size;
    }
  }
}
</style>
